/** Parse user-visible error text from JSON error bodies (FastAPI `{ "detail": "..." }`, etc.). */
function extractErrorMessageFromJson(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string') return value.trim().length ? value : null;
  if (typeof value !== 'object') return null;
  const obj = value as Record<string, unknown>;
  const candidates: unknown[] = [obj.error, obj.message, obj.detail, obj.title];
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim().length) return c.trim();
  }
  if (Array.isArray(obj.detail)) {
    const parts = obj.detail
      .map((d) => extractErrorMessageFromJson(d))
      .filter((s): s is string => typeof s === 'string' && s.trim().length > 0);
    if (parts.length) return parts.join('\n');
  }
  if (Array.isArray(obj.errors)) {
    const parts = obj.errors
      .map((d) => extractErrorMessageFromJson(d))
      .filter((s): s is string => typeof s === 'string' && s.trim().length > 0);
    if (parts.length) return parts.join('\n');
  }
  if (typeof obj.msg === 'string' && obj.msg.trim().length)
    return obj.msg.trim();
  return null;
}

/** Prefer server JSON message (e.g. 403 `detail`); otherwise use fallback. */
export async function getErrorMessageFromResponse(
  response: Response,
  fallback: string
): Promise<string> {
  try {
    const text = await response.text();
    const trimmed = text.trim();
    if (!trimmed) return fallback;
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      return extractErrorMessageFromJson(parsed) ?? fallback;
    } catch {
      return trimmed.length ? trimmed : fallback;
    }
  } catch {
    return fallback;
  }
}

/** When `response.ok` is false, throw `Error` with server JSON `detail` when present. */
export async function throwIfHttpError(
  response: Response,
  fallback: string
): Promise<void> {
  if (response.ok) return;
  const message = await getErrorMessageFromResponse(response, fallback);
  throw new Error(message);
}
